import React, { useEffect, useState } from "react";
import { api, formatNok } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";

const STATUSES = ["ny", "kontaktet", "avtalt", "fullført", "avbrutt"];
const LABEL = { ny: "Ny", kontaktet: "Kontaktet", avtalt: "Avtalt", "fullført": "Fullført", avbrutt: "Avbrutt" };
const STATUS_STYLE = {
  ny: "bg-warm/20 text-navy", kontaktet: "bg-electric/10 text-electric", avtalt: "bg-blue-100 text-blue-700",
  "fullført": "bg-green-100 text-green-700", avbrutt: "bg-red-100 text-red-700",
};

export default function PurchaseRequests() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/admin/purchase-requests").then((r) => setRows(r.data)).catch(() => {}); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/admin/purchase-requests/${id}/status`, { status });
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    toast.success("Status oppdatert");
  };
  const remove = async (id) => {
    if (!window.confirm("Slette denne kjøpsforespørselen?")) return;
    await api.delete(`/admin/purchase-requests/${id}`);
    setRows((rs) => rs.filter((r) => r.id !== id));
    toast.success("Slettet");
  };

  return (
    <div data-testid="admin-purchase-requests">
      <h1 className="font-heading text-2xl font-bold text-navy">Kjøpsforespørsler</h1>
      <p className="mt-1 text-sm text-ink/60">Forespørsler fra butikken · {rows.length} totalt</p>
      <div className="mt-6 space-y-3">
        {rows.length === 0 && <p className="text-ink/50 text-sm">Ingen kjøpsforespørsler ennå.</p>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-hairline bg-white shadow-sm" data-testid={`purchase-row-${r.id}`}>
            <div className="flex items-center justify-between gap-3 p-4 cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)}>
              <div className="min-w-0">
                <p className="font-semibold text-navy truncate">{r.name} · {r.product_name}</p>
                <p className="text-sm text-ink/60 truncate">{r.quantity} stk · {formatNok(r.total_price)} · {new Date(r.created_at).toLocaleString("nb-NO")}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[r.status] || "bg-slate-100"}`} data-testid="purchase-row-status">{LABEL[r.status] || r.status}</span>
                {open === r.id ? <ChevronUp className="h-4 w-4 text-ink/40" /> : <ChevronDown className="h-4 w-4 text-ink/40" />}
              </div>
            </div>
            {open === r.id && (
              <div className="border-t border-hairline p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {[["Telefon", r.phone], ["E-post", r.email], ["Produkt", r.product_name], ["Produkt-ID", r.product_id], ["Antall", r.quantity], ["Pris ved forespørsel", formatNok(r.unit_price)], ["Totalt", formatNok(r.total_price)], ["Melding", r.message || "–"], ["Mottatt", new Date(r.created_at).toLocaleString("nb-NO")]].map(([k, v]) => (
                  <div key={k}><span className="text-ink/50">{k}: </span><span className="font-medium text-navy break-all">{v}</span></div>
                ))}
                {r.product_slug && <a href={`/butikk/${r.product_slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-electric hover:underline sm:col-span-2"><ExternalLink className="h-3.5 w-3.5" /> Åpne produktsiden</a>}
                <div className="sm:col-span-2 mt-2 flex flex-wrap items-center gap-2">
                  <span className="text-ink/50">Status:</span>
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(r.id, s)} className={`rounded-full px-3 py-1 text-xs font-medium border ${r.status === s ? "border-electric bg-electric text-white" : "border-hairline text-navy hover:border-electric"}`} data-testid={`purchase-status-${s}`}>{LABEL[s]}</button>
                  ))}
                  <button onClick={() => remove(r.id)} className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid="purchase-delete"><Trash2 className="h-3.5 w-3.5" /> Slett</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
